import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Check, Phone, ArrowRight } from "lucide-react";
import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";
import { Button } from "@/components/ui/button";
import { Reveal } from "@/components/site/motion";
import { PAGES } from "@/data/pages";
import { CONTACT } from "@/data/content";
import useSEO from "@/hooks/useSEO";

const SITE = "https://newbeginningsrecovery.ca";

// Per-route SEO — targets different keyword clusters so each page ranks separately
const SEO_MAP = {
  "/admission/preparing-for-your-stay": {
    title: "Preparing for Rehab in Toronto | Admissions Checklist — New Beginnings",
    description:
      "How to prepare for inpatient drug & alcohol rehab in Toronto. Same-day admission, packing checklist, insurance & GreenShield coverage, arrival guide.",
    keywords: "prepare for rehab Toronto, admission checklist, what to bring to rehab, inpatient admission Ontario",
  },
  "/admission/during-your-stay": {
    title: "Life Inside Rehab — A Day at New Beginnings Recovery Toronto",
    description:
      "A day-in-the-life at our licensed inpatient addiction treatment home near the Scarborough Bluffs, Toronto — therapy, meals, movement, family visits.",
    keywords: "day in inpatient rehab Toronto, residential addiction treatment schedule, life inside rehab",
  },
  "/admission/financing-your-stay": {
    title: "Financing Rehab in Toronto — Private-Pay & GreenShield Coverage",
    description:
      "How to pay for inpatient addiction treatment in Toronto. Private-pay pricing, GreenShield direct-billing, Sun Life, Manulife, Canada Life, union & extended benefits.",
    keywords: "cost of rehab Toronto, GreenShield rehab coverage, private-pay addiction treatment, insurance for rehab Canada",
  },
  "/about/what-we-treat": {
    title: "What We Treat — Addictions & Mental-Health Conditions | Toronto Rehab",
    description:
      "New Beginnings Recovery treats alcohol, opioid, stimulant, cannabis and prescription-drug addictions alongside depression, anxiety, trauma and dual-diagnosis.",
    keywords: "alcohol addiction rehab Toronto, opioid addiction treatment, dual diagnosis rehab, trauma-informed care Ontario",
  },
  "/about/get-involved": {
    title: "Get Involved — Volunteer & Support New Beginnings Recovery Toronto",
    description:
      "Support recovery in Toronto. Volunteer, donate, refer a loved one, or partner with our licensed inpatient rehab in Scarborough Bluffs.",
    keywords: "volunteer rehab Toronto, donate to addiction recovery, refer someone to rehab",
  },
  "/programs/inpatient-treatment": {
    title: "Inpatient Drug & Alcohol Treatment Program — Toronto | 28-Day Residential Rehab",
    description:
      "28-day inpatient drug & alcohol rehab program in Toronto. Medical detox, individual therapy, group therapy, wellness, and 24/7 care with on-call clinical support. Private-pay & GreenShield.",
    keywords: "inpatient rehab Toronto, 28 day drug rehab, residential alcohol treatment, medical detox Ontario",
  },
  "/programs/aftercare": {
    title: "Aftercare & Alumni Program — Lifetime Recovery Support | Toronto Rehab",
    description:
      "Lifetime aftercare, alumni community, weekly meetings and relapse-prevention groups for graduates of our Toronto inpatient rehab program.",
    keywords: "rehab aftercare Toronto, sober support Ontario, alumni recovery community, relapse prevention",
  },
  "/programs/family-care": {
    title: "Family Program — Support for Loved Ones | Toronto Rehab & Addiction Recovery",
    description:
      "Family therapy, education and boundary coaching for the loved ones of clients in our Toronto inpatient addiction treatment centre.",
    keywords: "family therapy addiction Toronto, rehab family program, addiction support for loved ones",
  },
};

const ContentPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const page = PAGES[location.pathname];
  const seo = SEO_MAP[location.pathname] || {};
  useSEO({ ...seo, canonical: `${SITE}${location.pathname}` });

  useEffect(() => { window.scrollTo(0, 0); }, [location.pathname]);

  if (!page) {
    return (
      <div className="bg-sand min-h-screen">
        <Navbar />
        <div className="pt-52 pb-32 text-center px-5">
          <h1 className="font-serif text-5xl text-forest">Page coming soon</h1>
          <Button onClick={() => navigate("/")} className="mt-6 rounded-full bg-forest text-sand">Back home</Button>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div data-testid="content-page" className="bg-sand min-h-screen">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-40 md:pt-48 pb-20 md:pb-28 overflow-hidden">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10 grid lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <p className="text-sm uppercase tracking-[0.25em] text-terracotta mb-4">{page.eyebrow}</p>
            <h1 data-testid="content-title" className="font-serif text-5xl md:text-6xl lg:text-7xl text-forest tracking-tight leading-[0.98]">
              {page.title}
            </h1>
            <p className="mt-6 text-lg text-ink2 leading-relaxed max-w-xl">{page.intro}</p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Button onClick={() => navigate("/contact")} data-testid="content-cta" className="h-13 px-7 py-6 rounded-full bg-terracotta hover:bg-[#b45a3d] text-white font-semibold">
                Get Help Today <ArrowRight className="w-4 h-4 ml-1.5" />
              </Button>
              <a href={CONTACT.phoneHref} className="h-auto px-7 py-4 rounded-full border border-forest text-forest hover:bg-forest hover:text-sand flex items-center gap-2 font-semibold transition-colors">
                <Phone className="w-5 h-5" /> {CONTACT.phone}
              </a>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <img src={page.image} alt={page.title} className="w-full h-[360px] md:h-[520px] object-cover arch-frame" />
          </Reveal>
        </div>
      </section>

      {/* Body sections */}
      <section className="pb-8 md:pb-16">
        <div className="mx-auto max-w-[1000px] px-5 md:px-10 space-y-16">
          {page.sections.map((s, i) => (
            <Reveal key={i}>
              <h2 className="font-serif text-3xl md:text-4xl text-forest mb-5">{s.heading}</h2>
              <div className="space-y-4">
                {s.body.map((p, j) => (
                  <p key={j} className="text-lg text-ink2 leading-relaxed">{p}</p>
                ))}
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Bullets card */}
      {page.bullets && (
        <section className="pb-24 md:pb-32">
          <div className="mx-auto max-w-[1000px] px-5 md:px-10">
            <Reveal>
              <div className="rounded-3xl bg-sand2 border border-line p-8 md:p-12">
                <h3 className="font-serif text-2xl md:text-3xl text-forest mb-6">{page.bullets.heading}</h3>
                <ul className="grid sm:grid-cols-2 gap-4">
                  {page.bullets.items.map((it, i) => (
                    <li key={i} className="flex items-start gap-3 text-ink">
                      <span className="grid place-items-center w-6 h-6 rounded-full bg-forest text-sand shrink-0 mt-0.5">
                        <Check className="w-3.5 h-3.5" />
                      </span>
                      {it}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>
        </section>
      )}

      {/* CTA band */}
      <section className="bg-forest text-sand py-20 md:py-28">
        <div className="mx-auto max-w-[1000px] px-5 md:px-10 text-center">
          <h2 className="font-serif text-4xl md:text-6xl leading-[1.02]">Ready to start your recovery journey?</h2>
          <p className="mt-4 text-sand/75 max-w-xl mx-auto text-lg">Immediate admittance and shorter wait lists. We accept private pay and most insurance plans, including GreenShield.</p>
          <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
            <Button onClick={() => navigate("/contact")} className="h-13 px-7 py-6 rounded-full bg-terracotta hover:bg-[#b45a3d] text-white font-semibold">Request a confidential call</Button>
            <a href={CONTACT.phoneHref} className="h-auto px-7 py-4 rounded-full border border-sand/40 text-sand hover:bg-sand hover:text-forest flex items-center justify-center gap-2 font-semibold transition-colors">
              <Phone className="w-5 h-5" /> {CONTACT.phone}
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ContentPage;
